import { useState } from 'react';
import { useSelector } from 'react-redux';
import i18n from 'i18n-js';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { Alert } from '@helpers';
import { deduplicateByProperty } from '@components/search/operations/helpers';
import { selectSelectedItem } from '@reducers/machines';

import { runSendToMachine } from '@components/search/operations/components/StartJobPopup/helpers/runSendToMachine';
import { goToJobScreen } from '@components/search/operations/components/StartJobPopup/helpers/goToJobScreen';
import { goToLinkedNcFile } from '@components/search/operations/components/StartJobPopup/helpers/goToLinkedNcFile';
import { buildAddNcFileConfig } from '@components/search/operations/components/StartJobPopup/helpers/buildAddNcFileConfig';
import { MAX_VISIBLE_NC_FILES } from '@components/search/operations/components/StartJobPopup/constants';
import { SEND_STATUS } from '@helpers';
import type { NcFileListItem, SendingStorage, MessageStorage, ScaleFontProps } from '@components/search/operations/components/StartJobPopup/types';
import type { StartJobPopupProps } from '@components/search/operations/components/StartJobPopup';

export type UseManageStartJobPopupParams = Pick<StartJobPopupProps, 'item' | 'hide'> & Required<ScaleFontProps>;

export const useManageStartJobPopup = ({ item, hide, scale, font }: UseManageStartJobPopupParams) => {
  const dispatch = useAppDispatch();
  const selectedItem = useSelector(selectSelectedItem);

  const { linkedNcFiles = [] } = item;

  const [uploadingFiles, setUploadingFiles] = useState<NcFileListItem[]>([]);
  const [sendingStorage, setSendingStorage] = useState<SendingStorage>({});
  const [messageStorage, setMessageStorage] = useState<MessageStorage>({});

  const isFinish = Object.values(sendingStorage).some((i) => i === SEND_STATUS.Finish);

  const uploadFile = buildAddNcFileConfig({ item, scale, font, setUploadingFiles, setSendingStorage });

  const list = deduplicateByProperty(linkedNcFiles, uploadingFiles, 'ncFileName');

  // Past this many linked files the list becomes an internal scroll area so the popup stays within the screen.
  const shouldScrollNcFiles = list.length > MAX_VISIBLE_NC_FILES;

  const handleSendToMachine = (element: NcFileListItem) =>
    runSendToMachine({
      element,
      dispatch,
      machineId: selectedItem.id,
      productionOperationId: item.productionOperationId,
      setSendingStorage,
      setMessageStorage,
    });

  const handleGoToJobScreen = () => goToJobScreen({ productionOperationId: item.productionOperationId, machineId: selectedItem?.id, hide });

  // Confirms the link with a success toast, then redirects to the job's NC Programs tab with the just-linked file selected.
  const handleLinked = (ncFileId: number) => {
    Alert({ title: i18n.t('Linked'), type: 'success' });
    goToLinkedNcFile({ productionOperationId: item.productionOperationId, ncFileId, machineId: selectedItem?.id, hide });
  };

  return {
    sendingStorage,
    messageStorage,
    isFinish,
    uploadFile,
    list,
    shouldScrollNcFiles,
    handleSendToMachine,
    handleGoToJobScreen,
    handleLinked,
  };
};
